<template>
  <TemplateForm
    ref="asset"
    v-model:data="hmeEquipment"
    :errors="errors"
    :loading="loading"
    manageRegions="shaftV2ManageRegionsHme"
    :saving="saving"
    :textData="textData"
    :typeGroups="equipmentGroups"
    @save="save"
  />
</template>

<script setup>
import { onMounted, reactive, ref, computed } from 'vue';

// Utils
import useAjax from '@/functions/use_ajax';
import { useRouter } from 'vue-router';
import useUploader from '@/vendor/use_uploader';
import generateTextData from '@/functions/generate_text_data';

// Components
import TemplateForm from '@/components/form/hme_form_template.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, default: null },
  heavyMobileEquipmentId: { type: String, default: null },
});

const router = useRouter();
const uploader = useUploader();
const ajax = useAjax();
const initialHme = ref(null);
const hmeEquipment = reactive(createNewHme());
const errors = ref(null);
const saving = ref(false);
const loading = ref(false);
const equipmentGroups = ref([]);
const diagramImage = ref(null);
const asset = ref(null);
const mainKey = 'shafts.configuration.heavy_mobile_equipment';
const textDataArray = [
  'details_description',
  'details_title',
  'equipment_name',
  'equipment_name_placeholder',
  'type',
  'equipment_diagram_description',
  'equipment_diagram_title',
  'upload_diagram',
  'add_image',
  'change_diagram',
  'manage_regions',
  'new_type',
  'no_type'
];
let diagramUpload;

// All endpoints that this component uses
const ENDPOINTS = {
  hmeTypes: `/organisations/${props.organisationId}/shafts_v2/heavy_mobile_equipment_types`,
  hmeGroup: `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/heavy_mobile_equipments`,
  currentHme: `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/heavy_mobile_equipments/${props.heavyMobileEquipmentId}`,
  get post_or_patch() {
    return props.heavyMobileEquipmentId ? this.currentHme : this.hmeGroup;
  },
};

const textData = computed(() => {
  return generateTextData(mainKey, textDataArray);
});

onMounted(async() => {
  await loadTypes();
  await loadHme();
});

async function loadHme() {
  if (props.heavyMobileEquipmentId) {
    loading.value = true;
    let { ok, data } = await ajax(ENDPOINTS.currentHme);

    if (ok) {
      initialHme.value = data;
      Object.assign(hmeEquipment, data);
      diagramImage.value = data?.diagram_thumbnail_url;
      const fetchType = equipmentGroups.value?.filter((item) => item.value === data.heavy_mobile_equipment_type_id);
      hmeEquipment.type = fetchType[0];
      loading.value = false;
    }
  } else {
    initialHme.value = createNewHme();
  }
}

async function loadTypes() {
  let { ok, data } = await ajax(ENDPOINTS.hmeTypes);

  if (ok) {
    equipmentGroups.value = data.map((item) => ({
      label: item.name,
      value: item.id,
    }));
  }
}

function createNewHme() {
  return {
    name: '',
    type: null,
  };
}

const formatHmeForPost = async () => {
  let hmeParams = {
    name: hmeEquipment.name,
    diagram: hmeEquipment.diagram,
    ...formatHmeAttributes(),
  };

  diagramUpload = asset.value?.diagramUpload;
  if (diagramUpload) {
    let blob = await uploader.upload(diagramUpload);
    hmeParams.diagram = {
      source: blob.signed_id,
      source_filename: blob.filename,
    };
  }

  return { heavy_mobile_equipment: hmeParams };
};

function formatHmeAttributes() {
  if (hmeEquipment.type?.isNew) {
    return {
      heavy_mobile_equipment_type_id: 'new',
      new_heavy_mobile_equipment_type: hmeEquipment.type.label,
    };
  } else {
    return {
      heavy_mobile_equipment_type_id: hmeEquipment.type?.value || null,
    };
  }
}

async function save() {
  errors.value = null;
  saving.value = true;

  let method = props.heavyMobileEquipmentId ? 'PATCH' : 'POST';
  let { ok, data } = await ajax(ENDPOINTS.post_or_patch, {
    method,
    body: await formatHmeForPost(),
  });

  if (ok) {
    router.push({ name: 'shaftV2ConfigurationHme' });
  } else {
    errors.value = data;
  }

  saving.value = false;
}
</script>
